import { useState } from "react";
import { X, Pin, Archive, ArchiveRestore, BellRing, Ban, AlertTriangle, ChevronDown, Check } from "lucide-react";
import { useUIStore } from "../../stores/uiStore";
import { useChatFlagsStore } from "../../stores/chatFlagsStore";
import type { RawChat } from "../../lib/xmpp";

interface ChatDetailsProps {
	chat: RawChat;
	chatId: string;
	accountId: string;
	isGroup?: boolean;
	participants?: any[];
}

export const ChatDetails = ({ chat, chatId, accountId, isGroup, participants = [] }: ChatDetailsProps) => {
	const { setDetailPanelOpen } = useUIStore();
	const [bookmarked, setBookmarked] = useState(chat.isBookmarked);
	const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
	const [blockError, setBlockError] = useState<string | null>(null);
	const [reportedToast, setReportedToast] = useState(false);

	const archived = useChatFlagsStore((s) => s.isArchived(accountId, chatId));
	const muted = useChatFlagsStore((s) => s.isMuted(accountId, chatId));
	const reported = useChatFlagsStore((s) => s.isReported(accountId, chatId));
	const notifMode = useChatFlagsStore((s) => s.getNotifMode(accountId, chatId));
	const setArchived = useChatFlagsStore((s) => s.setArchived);
	const setMuted = useChatFlagsStore((s) => s.setMuted);
	const setReported = useChatFlagsStore((s) => s.setReported);
	const setNotifMode = useChatFlagsStore((s) => s.setNotifMode);

	const displayName = chat.getDisplayName() || chatId;
	const initials = displayName
		.split(" ")
		.map((w: string) => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

	const handleToggleBookmark = () => {
		const next = !bookmarked;
		(chat as any).setBookmarked(next);
		setBookmarked(next);
	};

	const handleToggleArchive = () => {
		setArchived(accountId, chatId, !archived);
	};

	const handleBlock = async () => {
		setBlockError(null);
		try {
			await (chat as any).block?.();
		} catch (err: any) {
			setBlockError(err?.message ?? "Block failed");
		}
	};

	const handleReport = () => {
		setReported(accountId, chatId, true);
		setMuted(accountId, chatId, true);
		setArchived(accountId, chatId, true);
		setReportedToast(true);
		setTimeout(() => setReportedToast(false), 3000);
	};

	const notifOptions: { value: "all" | "mentions" | "none"; label: string }[] = [
		{ value: "all", label: "All Messages" },
		{ value: "mentions", label: "Mentions Only" },
		{ value: "none", label: "None" },
	];

	return (
		<div className="w-72 h-full glass-panel border-l border-white/8 flex flex-col shrink-0 overflow-y-auto">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
				<h2 className="text-sm font-medium text-white">Details</h2>
				<button
					onClick={() => setDetailPanelOpen(false)}
					className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
					title="Close"
				>
					<X size={16} />
				</button>
			</div>

			{/* Contact Info */}
			<div className="flex flex-col items-center gap-2 px-4 py-6 border-b border-white/8">
				<div className="w-16 h-16 rounded-full bg-primary/50 flex items-center justify-center text-white text-xl font-medium">
					{initials}
				</div>
				<p className="text-sm font-medium text-white text-center">{displayName}</p>
				<p className="text-xs text-white/55 text-center break-all">{chatId}</p>
			</div>

			{/* Actions */}
			<div className="flex flex-col py-2">
				{/* Pin Chat */}
				<button
					onClick={handleToggleBookmark}
					className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/90 hover:bg-white/8 transition-colors text-left"
				>
					<Pin size={16} className="text-white/55 shrink-0" />
					<span>{bookmarked ? "Unpin Chat" : "Pin Chat"}</span>
				</button>

				{/* Archive Chat */}
				<button
					onClick={handleToggleArchive}
					className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/90 hover:bg-white/8 transition-colors text-left"
				>
					{archived ? (
						<ArchiveRestore size={16} className="text-white/55 shrink-0" />
					) : (
						<Archive size={16} className="text-white/55 shrink-0" />
					)}
					<span>{archived ? "Unarchive Chat" : "Archive Chat"}</span>
				</button>

				{/* Notifications */}
				<div className="relative">
					<button
						onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
						className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/90 hover:bg-white/8 transition-colors text-left w-full"
					>
						<BellRing size={16} className={muted || notifMode !== "all" ? "text-yellow-400 shrink-0" : "text-white/55 shrink-0"} />
						<span className="flex-1">Notifications</span>
						<span className="text-xs text-white/40 mr-1">
							{muted ? "Muted" : notifOptions.find((o) => o.value === notifMode)?.label}
						</span>
						<ChevronDown size={14} className="text-white/40 shrink-0" />
					</button>
					{notifDropdownOpen && (
						<div className="mx-4 mb-1 rounded-lg bg-white/10 border border-white/8 overflow-hidden">
							{notifOptions.map((opt) => (
								<button
									key={opt.value}
									onClick={() => {
										setNotifMode(accountId, chatId, opt.value);
										// "none" implies muted (no notifications at all)
										setMuted(accountId, chatId, opt.value === "none");
										setNotifDropdownOpen(false);
									}}
									className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
										notifMode === opt.value
											? "text-primary bg-white/5"
											: "text-white/80 hover:bg-white/5"
									}`}
								>
									{notifMode === opt.value && <Check size={12} />}
									<span>{opt.label}</span>
								</button>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Members (group chats) */}
			{isGroup && participants.length > 0 && (
				<div className="py-2 border-t border-white/8">
					<h3 className="text-xs text-white/40 uppercase tracking-wider mb-2 px-4">
						Members ({participants.length})
					</h3>
					{participants.map((p: any) => (
						<div key={p.jid || p.name || Math.random()} className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors">
							<div className="w-8 h-8 rounded-full bg-primary/30 flex items-center justify-center text-white text-xs font-medium shrink-0">
								{(p.name || p.jid || "?").slice(0, 2).toUpperCase()}
							</div>
							<div className="min-w-0">
								<p className="text-sm text-white/80 truncate">{p.name || p.jid}</p>
								{p.role && <p className="text-xs text-white/40">{p.role}</p>}
							</div>
						</div>
					))}
				</div>
			)}

			{/* Danger Zone */}
			<div className="flex flex-col py-2 border-t border-white/8">
				{/* Block */}
				<button
					onClick={handleBlock}
					className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-white/8 transition-colors text-left"
				>
					<Ban size={16} className="shrink-0" />
					<span>Block</span>
				</button>
				{blockError && (
					<p className="px-4 -mt-1 pb-2 text-xs text-red-400/80">{blockError}</p>
				)}

				{/* Report Spam */}
				<button
					onClick={handleReport}
					disabled={reported}
					className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-white/8 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<AlertTriangle size={16} className="shrink-0" />
					<span>{reported ? "Reported" : "Report Spam"}</span>
				</button>
				{reportedToast && (
					<p className="px-4 -mt-1 pb-2 text-xs text-yellow-400/80">
						Marked as spam — chat muted and archived.
					</p>
				)}
			</div>

			{/* Profile / JID section */}
			<div className="mt-auto px-4 py-4 border-t border-white/8">
				<p className="text-xs text-white/40 uppercase tracking-wider mb-2">Profile</p>
				<p className="text-xs text-white/55 break-all">{chatId}</p>
			</div>
		</div>
	);
};
